// 🔧 vendors_list.js
// ✅ Purpose: Manager/admin page to view and manage event vendors
// ✅ Features:
// - Uses the standard Let's Go Vote responsive manager page layout
// - Shows vendor summary cards
// - Groups vendors by configured vendor category
// - Displays vendors as responsive tiles
// - Shows vendor logo, website, description, category, booth number, and map status
// - Links to edit each vendor
// - Links to public vendor_view page for each vendor
// - Supports vendor delete with logo cleanup
// - Shows success/error notifications using top-page colored alerts
// - Shows helpful empty-state guidance when no vendors exist
// - Skips vendor reads and explains how to enable the Vendors Power Up when it is unavailable
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";
import { useEventContext } from "../../hooks/use_event_context";
import { db, storage, functions } from "../../firebase";
import {
  collection,
  query,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import { hasPurchasedAddOn } from "../../utils/event_addons";

import "react-quill/dist/quill.snow.css";
import { sanitizeHtml } from "../../utils/sanitize_html";

const VendorsList = () => {
  const { eventId, eventslug, eventDoc, eventLoading } = useEventContext();
  const vendorsEnabled = hasPurchasedAddOn(eventDoc, "vendors");

  const [vendors, setVendors] = useState([]);
  const [status, setStatus] = useState(null);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const getVendorEditPath = (vendor) =>
    `/${eventslug}/vendor/${vendor.vendorSlug || vendor.id}`;
  const getVendorViewPath = (vendor) =>
    `/${eventslug}/vendors/${vendor.vendorSlug || vendor.id}`;
  const getVendorSettingsPath = () => `/${eventslug}/vendors_settings`;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const loadCategories = async () => {
    if (!eventId || !vendorsEnabled) return;

    try {
      const settingsRef = doc(db, "events", eventId, "settings", "voting");
      const snap = await getDoc(settingsRef);

      if (snap.exists()) {
        const data = snap.data();

        if (Array.isArray(data.vendorCategories)) {
          setVendorCategories(data.vendorCategories.map((category) => category.name));
        }
      }
    } catch (err) {
      logger.warn("⚠️ Could not load vendor categories", {
        value: err,
      });

      showStatus(setStatus, {
        type: "warning",
        message: "Unable to load vendor categories.",
      });
    }
  };

  const fetchVendors = async () => {
    if (!eventId || !vendorsEnabled) {
      setVendors([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const vendorsQuery = query(
        collection(db, "events", eventId, "vendors"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(vendorsQuery);

      const results = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setVendors(results);

      logger.log("✅ Vendors loaded:", {
        value: results.length,
      });
    } catch (err) {
      logger.error("🔥 Error loading vendors:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load vendors.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventLoading || !eventId) return;

    if (!vendorsEnabled) {
      setVendors([]);
      setVendorCategories([]);
      setLoading(false);
      return;
    }

    loadCategories();
    fetchVendors();
    // These functions intentionally capture the current event entitlement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, eventLoading, vendorsEnabled]);

  const handleDelete = async (vendor) => {
    const vendorName = vendor.name || "Unnamed Vendor";

    if (!window.confirm(`Delete vendor '${vendorName}'?`)) return;

    try {
      await deleteDoc(doc(db, "events", eventId, "vendors", vendor.id));

      if (vendor.logoUrl) {
        const path = new URL(vendor.logoUrl).pathname;

        const cleanPath = decodeURIComponent(
          path.replace(/^\/v0\/b\/[^/]+\/o\//, "")
        );

        const fileRef = ref(storage, cleanPath);

        await deleteObject(fileRef);
      }

      showStatus(setStatus, {
        type: "warning",
        message: `Deleted vendor '${vendorName}'.`,
      });

      fetchVendors();
    } catch (err) {
      logger.error("🔥 Vendor delete error:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Error deleting vendor.",
      });
    }
  };

    const getVendorPaymentBadge = (vendor) => {
    if (
      vendor.paymentStatus === undefined &&
      vendor.approvalStatus === undefined &&
      vendor.isApproved === undefined
    ) {
      return (
        <span className="badge bg-light text-dark border">
          Legacy Vendor
        </span>
      );
    }

    if (vendor.paymentStatus === "paid" && vendor.isApproved === true) {
      return <span className="badge bg-success">Paid / Approved</span>;
    }

    if (vendor.paymentStatus === "pending") {
      return (
        <span className="badge bg-warning text-dark">
          Payment Pending
        </span>
      );
    }

    if (vendor.paymentStatus === "waived") {
      return <span className="badge bg-info text-dark">Fee Waived</span>;
    }

    if (vendor.paymentStatus === "not_required") {
      return (
        <span className="badge bg-secondary">
          No Payment Required
        </span>
      );
    }

    return (
      <span className="badge bg-light text-dark border">
        Payment Unknown
      </span>
    );
  };

  const handleSendPaymentLink = async (vendor) => {
    if (!eventId || !vendor?.id) return;

    if (!vendor.vendorContactEmail) {
      showStatus(setStatus, {
        type: "error",
        message: "Vendor contact email is required before sending a payment link.",
      });

      return;
    }

    if (!vendor.paymentRequired || !Number(vendor.amountCents || 0)) {
      showStatus(setStatus, {
        type: "warning",
        message: "This vendor does not have a payment amount configured.",
      });

      return;
    }

    try {
      showStatus(setStatus, {
        type: "info",
        message: `Sending payment link to ${vendor.vendorContactEmail}...`,
      });

      const createVendorCheckoutSession = httpsCallable(
        functions,
        "createVendorCheckoutSession"
      );

      await createVendorCheckoutSession({
        eventId,
        vendorId: vendor.id,
      });

      showStatus(setStatus, {
        type: "success",
        message: `Payment link sent to ${vendor.vendorContactEmail}.`,
      });

      fetchVendors();
    } catch (err) {
      logger.error("❌ Failed to send vendor payment link:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "Failed to send vendor payment link.",
      });
    }
  };

  if (eventLoading || loading) {
    return <FullPageSpinner message="Loading Vendors..." />;
  }

  if (!vendorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-warning" role="alert">
            Vendor management is not enabled for this event. Enable the Vendors Power Up before managing vendors.
          </div>
          <Link to={`/${eventslug}/event_package`} className="btn btn-primary">
            Open Event Package &amp; Add-ons
          </Link>
        </div>
      </section>
    );
  }

  const categoryMap = {};

  vendorCategories.forEach((categoryName) => {
    categoryMap[categoryName] = [];
  });

  const otherVendors = [];

  vendors.forEach((vendor) => {
    if (vendor.category && categoryMap[vendor.category]) {
      categoryMap[vendor.category].push(vendor);
    } else {
      otherVendors.push(vendor);
    }
  });

  const vendorDisplayGroups = [
    ...vendorCategories.map((categoryName) => ({
      categoryName,
      vendors: categoryMap[categoryName] || [],
    })),
    {
      categoryName: "Other Vendors",
      vendors: otherVendors,
    },
  ].filter((group) => group.vendors.length > 0);

  const totalVendors = vendors.length;

  const vendorsWithLogos = vendors.filter((vendor) => vendor.logoUrl).length;

  const mappedVendors = vendors.filter(
    (vendor) =>
      vendor.mapMarkerX !== null &&
      vendor.mapMarkerX !== undefined &&
      vendor.mapMarkerX !== "" &&
      vendor.mapMarkerY !== null &&
      vendor.mapMarkerY !== undefined &&
      vendor.mapMarkerY !== ""
  ).length;

  const featuredVendors = vendors.filter(
    (vendor) => vendor.featuredVendor === true
  ).length;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">Vendor List</h1>

            <p className="lead text-muted mb-0">
              Manage vendor tiles, booth numbers, public vendor pages, and vendor map locations for this event.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex flex-wrap gap-2 justify-content-lg-end align-items-start">
            <PromotionSourceButton source="vendors" sourceScope="collection" />
            <Link to={`/${eventslug}/vendor`} className="btn btn-primary">
              ➕ Add Vendor
            </Link>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Vendors</div>
                <div className="fs-3 fw-bold">{totalVendors}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Vendor Categories</div>
                <div className="fs-3 fw-bold">{vendorCategories.length}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Logos Added</div>
                <div className="fs-3 fw-bold">{vendorsWithLogos}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Map Locations</div>
                <div className="fs-3 fw-bold">{mappedVendors}</div>

                {featuredVendors > 0 && (
                  <div className="small text-success mt-1">
                    {featuredVendors} featured vendor
                    {featuredVendors !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Vendor Tools</strong>
          </div>

          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
              <div className="text-muted">
                Vendors are grouped by vendor category. Vendors without a valid configured category appear under <strong>Other Vendors</strong>.
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                <Link
                  to={`/${eventslug}/vendor`}
                  className="btn btn-primary"
                >
                  ➕ Add Vendor
                </Link>

                <Link
                  to={getVendorSettingsPath()}
                  className="btn btn-outline-secondary"
                >
                  ⚙️ Vendor Settings
                </Link>

                <Link
                  to={`/${eventslug}/vendors`}
                  className="btn btn-outline-primary"
                >
                  👁️ Public Vendors
                </Link>
              </div>
            </div>
          </div>
        </div>

        {vendors.length === 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h4 className="mb-3">🛍 Event Vendors</h4>

              <p>
                Vendors help attendees find food, merchandise, parts, services, clubs, artists, and other event resources.
              </p>

              {vendorCategories.length === 0 && (
                <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <strong>⚠ Vendor Categories Not Configured</strong>
                    <br />
                    Vendor categories help organize the public vendor directory.
                  </div>

                  <Link
                    to={getVendorSettingsPath()}
                    className="btn btn-warning btn-sm"
                  >
                    ⚙️ Configure Vendor Categories
                  </Link>
                </div>
              )}

              {vendorCategories.length > 0 && (
                <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <strong>⚠ No Vendors Added Yet</strong>
                    <br />
                    Your vendor categories are configured, but no vendors have been added.
                  </div>

                  <Link
                    to={`/${eventslug}/vendor`}
                    className="btn btn-warning btn-sm"
                  >
                    ➕ Add Vendor
                  </Link>
                </div>
              )}

              <div className="alert alert-light border">
                Vendors without a category will automatically be grouped into an <strong>Other Vendors</strong> section on the public vendor page.
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Link to={`/${eventslug}/vendor`} className="btn btn-primary">
                  ➕ Add Your First Vendor
                </Link>

                <Link
                  to={getVendorSettingsPath()}
                  className="btn btn-outline-secondary"
                >
                  ⚙️ Vendor Settings
                </Link>
              </div>
            </div>
          </div>
        )}

        {vendorDisplayGroups.map((group) => {
          return (
            <div key={group.categoryName} className="card shadow-sm mb-4">
              <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                  <strong>🛍 {group.categoryName}</strong>

                  <div className="small text-muted">
                    {group.vendors.length} vendor
                    {group.vendors.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="row g-4">
                  {group.vendors.map((vendor) => (
                    <div key={vendor.id} className="col-12 col-md-6 col-xl-4">
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body">
                          <div
                            className="border rounded bg-light d-flex align-items-center justify-content-center mb-3"
                            style={{
                              minHeight: "120px",
                              overflow: "hidden",
                            }}
                          >
                            {vendor.logoUrl ? (
                              <img
                                src={vendor.logoUrl}
                                alt={`${vendor.name || "Vendor"} logo`}
                                className="img-fluid"
                                style={{
                                  maxHeight: "100px",
                                  maxWidth: "90%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <span className="text-muted">No logo</span>
                            )}
                          </div>

                          <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                            <div>
                              <h5 className="mb-1">
                                {vendor.name || "Unnamed Vendor"}
                              </h5>

                              <div className="small text-muted">
                                {vendor.category || "Other Vendor"}
                              </div>

                              {vendor.boothNumber && (
                                <div className="small text-muted">
                                  📍 Booth: {vendor.boothNumber}
                                </div>
                              )}
                            </div>

                            {vendor.featuredVendor && (
                              <span className="badge bg-success">
                                Featured
                              </span>
                            )}
                          </div>

                          <div className="mb-3">
                            {vendor.url ? (
                              <a
                                href={vendor.url}
                                target="_blank"
                                rel="noreferrer"
                                className="small text-decoration-none"
                              >
                                🌐 Visit Website
                              </a>
                            ) : (
                              <span className="small text-muted">
                                No website
                              </span>
                            )}
                          </div>

                          <div className="mb-3">
                            {vendor.description ? (
                              <div
                                className="ql-editor line-clamp-3 p-0"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtml(vendor.description),
                                }}
                              />
                            ) : (
                              <span className="text-muted">
                                No description
                              </span>
                            )}
                          </div>

                          <div className="mb-3 d-flex flex-wrap gap-2">
                            {getVendorPaymentBadge(vendor)}

                            {vendor.vendorPaymentEmailSent && (
                              <span className="badge bg-primary">
                                Email Sent
                              </span>
                            )}
                          </div>

                          <div className="mb-3">
                            {vendor.mapMarkerX !== null &&
                            vendor.mapMarkerX !== undefined &&
                            vendor.mapMarkerX !== "" &&
                            vendor.mapMarkerY !== null &&
                            vendor.mapMarkerY !== undefined &&
                            vendor.mapMarkerY !== "" ? (
                              <span className="badge bg-primary">
                                🗺 Map Location Added
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark border">
                                No Map Location
                              </span>
                            )}
                          </div>

                          <div className="d-flex flex-wrap gap-2">
                            <PromotionSourceButton source="vendors" sourceId={vendor.id} className="btn btn-sm btn-outline-primary" />
                            <Link
                              to={getVendorViewPath(vendor)}
                              className="btn btn-sm btn-outline-primary"
                            >
                              👁️ View
                            </Link>

                            <Link
                              to={getVendorEditPath(vendor)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              ✏️ Edit
                            </Link>
                            {vendor.paymentRequired === true && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                onClick={() => handleSendPaymentLink(vendor)}
                                disabled={vendor.paymentStatus === "paid"}
                              >
                                {vendor.vendorPaymentEmailSent
                                  ? "🔁 Resend Payment Link"
                                  : "💳 Send Payment Link"}
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(vendor)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default VendorsList;
